import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MapPin, Radio, ShieldOff, Crosshair, Plus, Trash2, Search, X, Users } from "lucide-react";
import { toast } from "sonner";

export default function Attendance() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [callsheets, setCallsheets] = useState([]);
  const [records, setRecords] = useState([]);
  const [csForm, setCsForm] = useState({
    shoot_date: new Date().toISOString().slice(0, 10),
    call_time: "06:00", wrap_time: "20:00", location: "",
    geofence_lat: 19.0760, geofence_lng: 72.8777, geofence_radius_m: 200,
    crew_required: [],
    cast: [],
  });

  const loadCallsheets = async (pid) => {
    if (!pid) return;
    const { data } = await api.get(`/callsheets?project_id=${pid}`);
    setCallsheets(data);
    const att = await api.get(`/attendance?project_id=${pid}`);
    setRecords(att.data);
  };

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      if (r.data[0]) {
        setProjectId(r.data[0].id);
        loadCallsheets(r.data[0].id);
      }
    });
  }, []);

  const publish = async () => {
    if (!projectId) return toast.error("Select a project");
    try {
      const cleanCast = (csForm.cast || [])
        .filter((c) => (c.character_name || "").trim())
        .map((c) => ({
          character_name: (c.character_name || "").trim(),
          actor_name:     (c.actor_name || "").trim(),
          user_id:        c.user_id || null,
          call_time:      c.call_time || null,
          on_set_time:    c.on_set_time || null,
          notes:          (c.notes || "").trim(),
        }));
      await api.post("/callsheets/publish", {
        project_id: projectId, ...csForm, cast: cleanCast,
      });
      toast.success("Call sheet published. Silent push queued.");
      setCsForm((f) => ({ ...f, cast: [] }));
      loadCallsheets(projectId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addCastRow = () => {
    setCsForm((f) => ({
      ...f, cast: [...(f.cast || []), {
        character_name: "", actor_name: "", user_id: "",
        call_time: "", on_set_time: "", notes: "",
      }],
    }));
  };
  const updateCastRow = (idx, patch) => {
    setCsForm((f) => ({
      ...f,
      cast: (f.cast || []).map((c, i) => i === idx ? { ...c, ...patch } : c),
    }));
  };
  const removeCastRow = (idx) => {
    setCsForm((f) => ({
      ...f,
      cast: (f.cast || []).filter((_, i) => i !== idx),
    }));
  };

  const wrap = async (csId) => {
    await api.post(`/callsheets/${csId}/wrap`);
    toast.success("Wrap logged. Geo tracker TERMINATED.");
    loadCallsheets(projectId);
  };

  const ping = async () => {
    if (!projectId) return;
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const res = await api.post("/attendance/ping", {
          project_id: projectId,
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        toast.success(res.data.inside_geofence ? "Inside geofence ✓" : `Outside (${res.data.distance_m}m)`);
        loadCallsheets(projectId);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }, (e) => toast.error("GPS denied"));
  };

  const activeCs = callsheets.find((c) => c.geo_tracker_state !== "terminated");

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="attendance-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Privacy-First Geo-Attendance</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">Call → Wrap Tracker</h1>
        <p className="mt-3 text-sm text-white/55 max-w-2xl">
          GPS is 100% dormant outside the window between Call Time and Wrap.
          No background tracking. No silent surveillance.
        </p>
      </div>

      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">Project</Label>
      <Select value={projectId} onValueChange={(v) => { setProjectId(v); loadCallsheets(v); }}>
        <SelectTrigger className="bg-black/40 border-white/15 max-w-md mt-2" data-testid="att-project">
          <SelectValue placeholder="Select project" />
        </SelectTrigger>
        <SelectContent>
          {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Active tracker pill */}
      <div className="mt-6 flex items-center gap-3" data-testid="tracker-state">
        {activeCs ? (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--association-theme-soft)] border" style={{ borderColor: "var(--association-theme)" }}>
            <Radio size={14} className="animate-pulse" style={{ color: "var(--association-theme)" }} />
            <span className="text-[10px] tracking-[0.28em] uppercase">Tracker {activeCs.geo_tracker_state}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <ShieldOff size={14} className="text-white/50" />
            <span className="text-[10px] tracking-[0.28em] uppercase text-white/50">GPS Dormant</span>
          </div>
        )}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* Publish call sheet */}
        <div className="cs-card rounded-2xl p-6">
          <div className="font-display text-xl uppercase tracking-tight mb-4">Publish Call Sheet</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Shoot Date">
              <Input type="date" value={csForm.shoot_date} data-testid="cs-date"
                onChange={(e) => setCsForm({ ...csForm, shoot_date: e.target.value })}
                className="bg-black/40 border-white/15 h-10" />
            </Field>
            <Field label="Call Time">
              <Input type="time" value={csForm.call_time} data-testid="cs-call"
                onChange={(e) => setCsForm({ ...csForm, call_time: e.target.value })}
                className="bg-black/40 border-white/15 h-10" />
            </Field>
            <Field label="Wrap Time">
              <Input type="time" value={csForm.wrap_time} data-testid="cs-wrap"
                onChange={(e) => setCsForm({ ...csForm, wrap_time: e.target.value })}
                className="bg-black/40 border-white/15 h-10" />
            </Field>
            <Field label="Radius (m)">
              <Input type="number" value={csForm.geofence_radius_m} data-testid="cs-radius"
                onChange={(e) => setCsForm({ ...csForm, geofence_radius_m: parseInt(e.target.value) || 200 })}
                className="bg-black/40 border-white/15 h-10" />
            </Field>
            <Field label="Location" full>
              <Input value={csForm.location} data-testid="cs-loc"
                onChange={(e) => setCsForm({ ...csForm, location: e.target.value })}
                className="bg-black/40 border-white/15 h-10" />
            </Field>
            <Field label="Geo-fence Lat">
              <Input type="number" step="0.0001" value={csForm.geofence_lat} data-testid="cs-lat"
                onChange={(e) => setCsForm({ ...csForm, geofence_lat: parseFloat(e.target.value) || 0 })}
                className="bg-black/40 border-white/15 h-10" />
            </Field>
            <Field label="Geo-fence Lng">
              <Input type="number" step="0.0001" value={csForm.geofence_lng} data-testid="cs-lng"
                onChange={(e) => setCsForm({ ...csForm, geofence_lng: parseFloat(e.target.value) || 0 })}
                className="bg-black/40 border-white/15 h-10" />
            </Field>
          </div>

          {/* Cast section — call-sheet cast list. Each row hydrates to
              the actor's active continuity card on the project dashboard. */}
          <div className="mt-6 pt-5 border-t border-white/10" data-testid="cs-cast-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 inline-flex items-center gap-1">
                  <Users size={11} /> Cast on Set Today
                </div>
                <div className="text-[11px] text-white/45 mt-0.5">
                  {(csForm.cast || []).length === 0
                    ? "Optional — add rows so HODs see continuity per cast member."
                    : `${(csForm.cast || []).length} row${(csForm.cast || []).length === 1 ? "" : "s"}`}
                </div>
              </div>
              <Button onClick={addCastRow}
                      className="btn-themed h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em]"
                      data-testid="cs-cast-add">
                <Plus size={11} className="mr-1" /> Add cast
              </Button>
            </div>
            {(csForm.cast || []).length > 0 && (
              <div className="space-y-3" data-testid="cs-cast-list">
                {(csForm.cast || []).map((row, i) => (
                  <CastRowEditor
                    key={i}
                    row={row}
                    idx={i}
                    onChange={(patch) => updateCastRow(i, patch)}
                    onRemove={() => removeCastRow(i)}
                  />
                ))}
              </div>
            )}
          </div>

          <Button onClick={publish} className="btn-themed w-full mt-5 h-11 rounded-full text-xs uppercase tracking-[0.25em]" data-testid="publish-cs-btn">
            <MapPin size={14} className="mr-2" /> Publish + Queue Silent Push
          </Button>
        </div>

        {/* Ping */}
        <div className="cs-card rounded-2xl p-6">
          <div className="font-display text-xl uppercase tracking-tight mb-2">Crew Check-In</div>
          <p className="text-sm text-white/55 mb-4">
            One tap. Sends your browser location to the active call sheet geo-fence.
            Returns inside/outside + distance from the geo-fence center.
          </p>
          <Button onClick={ping} disabled={!activeCs} className="btn-themed w-full h-12 rounded-full uppercase tracking-[0.25em] text-xs" data-testid="ping-btn">
            <Crosshair size={14} className="mr-2" /> {activeCs ? "Send Geo Ping" : "Tracker Dormant"}
          </Button>

          <div className="mt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Recent pings</div>
            <div className="space-y-2 max-h-80 overflow-auto" data-testid="ping-list">
              {records.length === 0 && <div className="text-xs text-white/40">No pings yet.</div>}
              {records.map((r) => (
                <div key={r.id} className="flex items-center gap-3 border border-white/5 rounded-lg p-3">
                  <div className={`w-2 h-2 rounded-full ${r.inside_geofence ? "bg-emerald-400" : "bg-red-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{r.user_name}</div>
                    <div className="text-[11px] text-white/45">{new Date(r.logged_at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs tabular-nums" style={{ color: r.inside_geofence ? "#34d399" : "#f87171" }}>
                    {r.distance_m}m
                  </div>
                </div>
              ))}
            </div>
          </div>

          {activeCs && (
            <Button onClick={() => wrap(activeCs.id)} variant="outline"
              className="w-full mt-5 h-10 bg-transparent border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-[0.25em] text-xs"
              data-testid="wrap-btn">
              Log Wrap (Terminate Tracker)
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


function CastRowEditor({ row, idx, onChange, onRemove }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setResults([]); return; }
    let dead = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/crew/lookup?q=${encodeURIComponent(q)}`);
        if (!dead) setResults(data.results || []);
      } catch { /* silent */ }
      finally { if (!dead) setBusy(false); }
    }, 260);
    return () => { dead = true; clearTimeout(t); };
  }, [q]);

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3"
         data-testid={`cs-cast-row-${idx}`}>
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={row.character_name || ""}
          onChange={(e) => onChange({ character_name: e.target.value })}
          placeholder="Character *"
          className="bg-black/40 border-white/15 h-10 text-sm"
          data-testid={`cs-cast-char-${idx}`}
        />
        <Input
          value={row.actor_name || ""}
          onChange={(e) => onChange({ actor_name: e.target.value })}
          placeholder="Actor name"
          className="bg-black/40 border-white/15 h-10 text-sm"
          data-testid={`cs-cast-actor-${idx}`}
        />
      </div>

      {row.user_id ? (
        <div className="mt-2 h-9 px-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between text-xs"
             data-testid={`cs-cast-linked-${idx}`}>
          <span className="text-white/80 inline-flex items-center gap-1">
            <Users size={11} className="text-emerald-300" />
            Platform user linked
          </span>
          <button onClick={() => onChange({ user_id: "" })}
                  className="text-white/45 hover:text-red-300"
                  data-testid={`cs-cast-unlink-${idx}`}>
            <X size={11} />
          </button>
        </div>
      ) : (
        <div className="relative mt-2">
          <div className="relative">
            <Search size={11}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Link platform actor by name / phone (optional)"
              className="h-9 pl-8 bg-black/40 border-white/15 text-xs"
              data-testid={`cs-cast-search-${idx}`}
            />
          </div>
          {open && q.trim().length >= 2 && (
            <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-white/15 bg-[#0a0a0c] shadow-xl max-h-56 overflow-y-auto"
                 data-testid={`cs-cast-results-${idx}`}>
              {busy ? (
                <div className="p-2 text-[10px] text-white/50">Searching…</div>
              ) : results.length === 0 ? (
                <div className="p-2 text-[10px] text-white/50">No match.</div>
              ) : (
                results.map((u) => (
                  <button key={u.id} type="button"
                          onClick={() => {
                            onChange({
                              user_id: u.id,
                              actor_name: row.actor_name || u.name || "",
                            });
                            setOpen(false); setQ("");
                          }}
                          className="w-full text-left px-2 py-1.5 hover:bg-white/8 border-b border-white/5 last:border-0"
                          data-testid={`cs-cast-result-${idx}-${u.id}`}>
                    <div className="text-xs text-white">{u.name || "Unnamed"}</div>
                    <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">
                      {u.role_label || u.primary_role || ""}
                      {u.phone ? ` · ${u.phone}` : ""}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 grid grid-cols-3 gap-2">
        <Input type="time" value={row.call_time || ""}
               onChange={(e) => onChange({ call_time: e.target.value })}
               className="bg-black/40 border-white/15 h-9 text-xs"
               data-testid={`cs-cast-call-${idx}`}
               placeholder="Call" />
        <Input type="time" value={row.on_set_time || ""}
               onChange={(e) => onChange({ on_set_time: e.target.value })}
               className="bg-black/40 border-white/15 h-9 text-xs"
               data-testid={`cs-cast-onset-${idx}`}
               placeholder="On-set" />
        <Input value={row.notes || ""}
               onChange={(e) => onChange({ notes: e.target.value })}
               placeholder="Notes"
               className="bg-black/40 border-white/15 h-9 text-xs"
               data-testid={`cs-cast-notes-${idx}`} />
      </div>

      <div className="mt-2 flex justify-end">
        <button onClick={onRemove}
                className="text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-red-300 inline-flex items-center gap-1"
                data-testid={`cs-cast-remove-${idx}`}>
          <Trash2 size={10} /> Remove row
        </button>
      </div>
    </div>
  );
}
